/*
 * WELLF — PDP behavioural event instrumentation  (ADDITIVE, isolated)
 * ---------------------------------------------------------------------------
 * Fires custom intent/objection/trust/engagement events to BOTH sinks:
 *   Meta:  fbq('trackCustom', '<MetaName>', params)   (PascalCase names)
 *   GA4:   gtag('event', '<ga4_name>', params)         (snake_case, wellf_ prefix)
 *
 * On this store the Meta pixel + GA4 are Shopify Web Pixels: window.fbq/gtag are
 * absent until MARKETING + ANALYTICS consent is granted, at which point they
 * appear on window. We fire ONLY post-consent, so the direct calls above work.
 *
 * CONSENT API — READ BEFORE TOUCHING consentOK()
 * Do NOT assume window.Shopify.customerPrivacy is put there for us. From 2026-08-24
 * Shopify's privacy banner stopped installing the real Customer Privacy API and now
 * writes only a method-less {cachedConsent, consentStatus} stub, so consentOK() failed
 * closed and EVERY wellf_* event silently died on both sinks for three days. We now
 * request the API explicitly via Shopify.loadFeatures() below. consentOK() is unchanged
 * and still fails closed if it cannot load — that is deliberate, never weaken it.
 *
 * SAFETY / ISOLATION
 *  - Loaded on the PRODUCT template only, and ONLY when settings.enable_wellf_events
 *    is on (so it's a pure kill-switch, default OFF). This file adds nothing to the
 *    page and mutates no shared state; it only reads the DOM + calls fbq/gtag.
 *  - Everything namespaced under this IIFE. No storage (in-memory Set for fire-once).
 *  - Consent-aware: never fires without live marketing+analytics consent; never
 *    queues/replays pre-consent behaviour (a behaviour that completes pre-consent is
 *    dropped, not stored).
 *  - Defensive throughout: a missing element or sink is a no-op, never an error.
 *
 * Event names + trigger semantics are FIXED CONTRACTS. When a name must change to
 * reflect what it actually measures, record it in the map below so analytics
 * history stays interpretable (raw-truth rule) — never silently repurpose a name.
 *
 * EVENT-NAME MAP — renames + removals (most recent first):
 *   2026-09-30 - BUY TAP + WELCOME FAILURES. ADDED, nothing renamed or retired:
 *       AddTapped / wellf_add_tapped - the add-to-bag control was tapped. `control` =
 *         rail|fab, `tap_index` counts repeats. This is the ATTEMPT; add_to_cart (from
 *         Shopify's pixel) is the RESULT, so the difference between them is the answer
 *         to "did the buy path work", which until now could not be measured at all.
 *         Undercounts slightly on controls that navigate away, never overcounts.
 *       WelcomeAutoFailed / wellf_welcome_auto_failed - the trigger qualified a visitor
 *         and the modal then failed to open. Previously silent AND it burned the offer
 *         for that visitor permanently. Once per pageview.
 *       WelcomeUseFailed / wellf_welcome_use_failed - claimed, tapped use, and the cart
 *         add did not land. Previously they were redirected to an empty bag holding a
 *         discount, with nothing recorded. Once per pageview.
 *   2026-09-01 — NEW PDP GEOMETRY. The taste reel band was inserted between the buy
 *     card and the formula section, the reel teaser went inside the reviews block, the
 *     "Still thinking?" outlet was removed and the reassurance grid went 2x2 to 2x1.
 *     Measured effect on page height, full lazy-load pass, same protocol as 31 Aug:
 *       mobile  6,983 -> 8,177  (+1,194, +17%)   scrollable 6,229 -> 7,333
 *       desktop 5,488 -> 6,407  (+919,  +17%)    scrollable 4,720 -> 5,507
 *     CONSEQUENCES, none of which are bugs but all of which break comparability:
 *       scroll_25/50/75/100 — the same percentage is now a different place on the page.
 *         Mobile scroll_50 was 3,115 (past the formula); it is now 3,667, inside the new
 *         band. Do NOT compare scroll-depth rates across 1 Sep.
 *       DeepEvaluator — its 40% gate is now scrollY 2,933 on mobile, was 2,492. Same
 *         definition, different content behind it.
 *       reviews_reached — REBOUND, see below. It also now leads scroll_25 by ~790px on
 *         mobile and ~979px on desktop, up from ~394px and ~613px, because the page grew
 *         underneath it. The lead is inherent to a viewport-entry event; it is not drift.
 *   2026-09-01 — REBOUND:
 *     ReviewsReached / wellf_reviews_reached — was bound to .prv with threshold 0.2. The
 *       teaser is now the first child of .prv, which took that box from 185px to 326px on
 *       mobile, so the event started firing when the TEASER entered view, about 150px
 *       before the reviews. Now bound to .prv__head, the reviews heading. Rows before and
 *       after 1 Sep both mean "reviews entered view", so the series stays readable, but
 *       the few hours in between lean early.
 *   2026-09-01 — RETIRED:
 *     HesitationDeclared / wellf_hesitation_declared (wellf-sensors.js) — the "Still
 *       thinking?" outlet was removed from the buy box. See that file's header.
 *   2026-09-04 - WELCOME CREDIT. The £5 new-customer offer under the plan cards.
 *     ADDED, a five step funnel plus its drop-off:
 *       WelcomeLineClick / wellf_welcome_line_click - the line was tapped, modal opened.
 *       WelcomeClaim / wellf_welcome_claim - email submitted, the card flipped.
 *       WelcomePlanToggle / wellf_welcome_plan_toggle - plan changed INSIDE the modal.
 *         `plan` = subscribe|one_time. Capped, debounced, change-only, like PlanToggler.
 *       WelcomeUse / wellf_welcome_use - left for the cart. `plan` is what they left
 *         with, `plan_changed` whether they moved off the subscribe default, `ms` the
 *         time in the modal.
 *       WelcomeDismiss / wellf_welcome_dismiss - closed without using. `stage` splits
 *         "never claimed" from "claimed and still did not go". Once per stage.
 *       WelcomeEmailError / wellf_welcome_email_error - the address failed validation.
 *     The email value is NEVER a parameter here, on any of them. Same rule as wellf_copy.
 *     These are dispatched by assets/wellf-welcome.js as wellf:welcome CustomEvents and
 *     graded here, so consent gating stays in one file.
 *   2026-09-01 - TASTE REEL. The social-proof reel is now the only measured video on
 *     the page. ADDED:
 *       ReelTeaserClick / wellf_reel_teaser_click - the buy-box thumbnail was tapped.
 *       ReelStart / wellf_reel_start - playback actually began. `via` says whether a
 *         teaser click preceded it; `sound` says whether audio was allowed.
 *       ReelProgress / wellf_reel_progress - pct 10/25/50/75/95 of duration reached,
 *         monotonic, once each. 10 is the accidental-tap filter.
 *       ReelWatch / wellf_reel_watch - one summary at exit: max_pct, watched_ms,
 *         plays, completed.
 *     RETIRED, stop firing:
 *       PDPVideoPlay / wellf_pdp_video_play - replaced by reel_start plus the pct 10
 *         step, which say the same thing with the depth attached. It has fired ZERO
 *         times since the 31 Aug detach, so nothing is lost; every row before that date
 *         is scroll-proxy data and must be read as "reached ~50% page depth".
 *     NOT MEASURED, deliberately: the formula-section gummy loop. wellf-product.js
 *       autoplays it from an IntersectionObserver on both breakpoints, so any event on
 *       it measures scrolling, not interest. It carries no engagement attribute and
 *       nothing binds to it. Do not add one.
 *   2026-08-31 — INTENT INSTRUMENTATION v2 (A1/A2/A3):
 *     RETIRED, stop firing; historical rows stay readable per framework S4B:
 *       PriceSurvivor / wellf_price_survivor — Jaccard 0.92 with scroll_50. Fired when
 *         the product column's bottom passed 200px above the viewport = 45.4% depth on
 *         mobile, 46.6% on desktop. A scroll-depth proxy wearing an intent name.
 *         Read historical rows as "reached ~45%".
 *       PriceReturn / wellf_price_return — fired on ANY part of the 1,900px column
 *         re-entering the viewport, with no direction test. Read historical rows as
 *         "upward re-entry to the product column". Replaced by zone re-entry counts.
 *     RENAMED + REBOUND so pre/post rows can never be conflated:
 *       BuyBoxDwell / wellf_buybox_dwell -> PlanCardDwell / wellf_plancard_dwell.
 *         Was the whole product column (1,915px mobile / 1,807px desktop) with an
 *         unreachable intersectionRatio>=0.5 branch, so it qualified anywhere across a
 *         ~1,900px band for 5s. Now the ~212px plan-card container, >=75% visible for
 *         >=1,500ms of VISIBLE time. Historical rows = "product-area dwell", not price.
 *       ReviewExpand / wellf_review_expand -> ReviewNav / wellf_review_nav.
 *         [data-rev-expand] matched zero elements in the live DOM, so the expand path
 *         never existed; it only ever recorded slider arrow clicks. Now named for that.
 *     CLOCK:
 *       DeepEvaluator / wellf_deep_evaluator — its 30s is now VISIBLE time, not wall
 *         clock. Pre-switch rows over-count backgrounded tabs; expect the rate to drop.
 *   2026-08-31 — TRIGGER DETACHED:
 *     PDPVideoPlay / wellf_pdp_video_play — no longer bound to the ambient formula
 *       video. wellf-product.js play()s that video from an IntersectionObserver at 25%
 *       visibility, so the event fired on SCROLL and recorded "reached ~50% page depth",
 *       not intent. ALL rows before this date are scroll-proxy data and must not be read
 *       as video engagement. The name is reserved for the coming taste-reactions video
 *       and now requires an explicit opt-in attribute + a user gesture + 2s of playback.
 *       SUPERSEDED 2026-09-01: this name is not reserved for the reel after all, it is
 *       RETIRED. See the TASTE REEL entry above. The reel is measured by wellf_reel_start
 *       and wellf_reel_progress instead, which carry the depth this name never did.
 *       The delegated capture-phase binding described there exists because the reel
 *       builds its <video> on first tap; a query-once binding at init finds nothing.
 *   2026-08-27 — TRIGGER CHANGE (name unchanged):
 *     PlanToggler / wellf_plan_toggle — now fires only when the selected plan actually
 *       CHANGES, and at most PLAN_TOGGLE_MAX times per pageview. It previously fired on
 *       every click of either button, including re-clicking the plan that was already
 *       selected, with no cap — one session produced 28 across 2 pageviews, which drowns
 *       the signal at our volume. A `toggle_index` param (1..MAX) now says which toggle
 *       it was. Counts before and after this date are NOT comparable.
 *     NOT CHANGED, deliberately: BuyBoxDwell / wellf_buybox_dwell is once per PAGEVIEW,
 *       not per session. The "11 in one session" reading was 11 pageviews in one session,
 *       which is the event working as designed. Do not add a session-level guard.
 *   2026-08-16 — REMOVED:
 *     WheelCodeCopied / wellf_wheel_code_copied — removed from the catalogue. It was
 *       structurally impossible: wheel codes are delivered by email via Klaviyo (enter
 *       email → submit → Klaviyo sends the code); there is no on-page code or copy UI to
 *       instrument. The single 2026-08-13 firing was a misbound selector, not a real event.
 *       WheelEmailEntered (fired on the valid-email submit) is the code-claim signal. Do
 *       not re-add this event.
 *   2026-08-15 — renames:
 *     FAQ_Melting        → FAQ_TrustGummies   (GA4 wellf_faq_stability → wellf_faq_trust_gummies)
 *       binds FAQ q2 "Why don't people trust creatine gummies? Are they a gimmick?"
 *       — there is NO melting FAQ; the old name never described its real trigger.
 *     FAQ_IsItJustASweet → FAQ_SweetVsGummy   (GA4 wellf_faq_efficacy → wellf_faq_sweet_vs_gummy)
 *       binds FAQ q3 "What's the difference between a sweet and a gummy?" (sweet-vs-gummy education).
 */
(function () {
  'use strict';

  var CS = document.currentScript;
  var DEBUG = !!(CS && CS.getAttribute('data-debug') === 'true');
  // Only run on the product page (belt-and-braces; it's only loaded there anyway).
  if (!document.querySelector('.pdp, [data-wellf-buybox], #pdpTrack')) return;

  // ── fire-once registry (in-memory only) ──
  var fired = Object.create(null);

  // ── ask Shopify for the real Customer Privacy API (see CONSENT API note up top).
  // Async and best-effort: consentOK() is re-evaluated per event, so it simply starts
  // returning true once the API lands. A behaviour that completes before then is
  // dropped, not queued — same rule as pre-consent behaviour. ──
  try {
    if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }]);
    }
  } catch (e) {}

  // ── consent: require BOTH marketing and analytics ──
  function consentOK() {
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      if (!cp || !cp.marketingAllowed || !cp.analyticsProcessingAllowed) return false;
      return cp.marketingAllowed() === true && cp.analyticsProcessingAllowed() === true;
    } catch (e) { return false; }
  }

  // ── Meta pixel: trackCustom sends nothing unless our pixel is registered on the
  // window-level fbq. It often already IS by the time we fire, so init only when it
  // isn't (re-initialising an already-live pixel is avoidable noise). init() sends no PageView
  // either way, so there's no duplication against the sandbox pixel. ──
  var META_ID = '1564332575347790';
  var metaInited = false;
  function metaReady() {
    if (typeof window.fbq !== 'function') return false;
    if (metaInited) return true;
    try {
      var state = window.fbq.getState && window.fbq.getState();
      var pixels = (state && state.pixels) || [];
      var already = false;
      for (var i = 0; i < pixels.length; i++) { if (pixels[i] && pixels[i].id === META_ID) { already = true; break; } }
      if (!already) window.fbq('init', META_ID);
      metaInited = true;
    } catch (e) { return false; }
    return true;
  }

  // ── the one helper that fires to both sinks ──
  function track(metaName, ga4Name, params) {
    if (!consentOK()) return false;
    var p = params ? Object.assign({}, params) : {};
    p.page = 'pdp';
    try { if (metaReady()) window.fbq('trackCustom', metaName, p); } catch (e) {}
    try { if (typeof window.gtag === 'function') window.gtag('event', ga4Name, p); } catch (e) {}
    if (DEBUG) { try { console.log('[wellf-events]', metaName, '/', ga4Name, p); } catch (e) {} }
    return true;
  }
  // fire-once wrapper: only marks fired when it actually fires (consent granted)
  function once(key, metaName, ga4Name, params) {
    if (fired[key]) return;
    if (track(metaName, ga4Name, params)) fired[key] = true;
  }
  // debounced repeatable fire (for plan toggle + gallery swipe).
  // Returns true only when it actually DELIVERED (not debounced away, consent granted),
  // so callers can count real sends. Callers that don't care can ignore it.
  function debounced(gapMs) {
    var last = -Infinity;   // the FIRST call always fires (even within gapMs of page load); matches wellf-sensors.js
    return function (metaName, ga4Name, params) {
      var now = perfNow();
      if (now - last < gapMs) return false;
      last = now;
      return track(metaName, ga4Name, params);
    };
  }

  function perfNow() { return (window.performance && performance.now) ? performance.now() : Date.now(); }
  function throttle(fn, ms) {
    var t = 0, queued = false;
    return function () {
      var now = perfNow();
      if (now - t >= ms) { t = now; fn(); }
      else if (!queued) { queued = true; setTimeout(function () { queued = false; t = perfNow(); fn(); }, ms - (now - t)); }
    };
  }

  // =========================================================================
  // SCROLL DEPTH  ·  Scroll25/50/75/100  (fire on CROSSING, so pre-consent
  // crossings are simply dropped, never replayed)
  // =========================================================================
  var maxPct = 0;
  var DEPTHS = [
    { t: 25, m: 'Scroll25', g: 'wellf_scroll_25' },
    { t: 50, m: 'Scroll50', g: 'wellf_scroll_50' },
    { t: 75, m: 'Scroll75', g: 'wellf_scroll_75' },
    { t: 100, m: 'Scroll100', g: 'wellf_scroll_100' }
  ];
  function scrollPct() {
    var doc = document.documentElement;
    var scrollable = (doc.scrollHeight - window.innerHeight);
    if (scrollable <= 0) return 100;
    return Math.min(100, Math.round((window.scrollY / scrollable) * 100));
  }

  // =========================================================================
  // THE CLOCK  ·  one visible-time accumulator, used by every time-based instrument
  // (A2, 2026-08-31). Declared here because PlanCardDwell and DeepEvaluator both read
  // it and both live above the engagement block. "Visible" is visibilitychange-gated:
  // a backgrounded tab stops accruing. Interaction-gating (Clarity's "active time",
  // ~4x lower) was considered and deliberately rejected for now.
  // =========================================================================
  var visibleMs = 0, lastTick = perfNow(), visible = (document.visibilityState !== 'hidden');
  function accrue() { var now = perfNow(); if (visible) visibleMs += now - lastTick; lastTick = now; }

  // =========================================================================
  // DEPTH STATE  ·  feeds DeepEvaluator
  // RETIRED 2026-08-31 (A3): PriceSurvivor and PriceReturn. Survivor was Jaccard 0.92
  // with scroll_50 - it fired when the buy-box bottom passed 200px above the viewport,
  // i.e. at 45.4% depth on mobile and 46.6% on desktop, so it was a scroll-depth proxy
  // wearing an intent name. PriceReturn fired on ANY part of the 1,900px-tall product
  // column re-entering the viewport, with no direction test. Zone re-entry counts
  // (z_plancards_re) replace it properly. Historical rows stay, read per framework S4B.
  // =========================================================================
  var reached40 = false;         // for DeepEvaluator

  function onScroll() {
    var pct = scrollPct();
    if (pct > maxPct) {
      // depth crossings
      for (var i = 0; i < DEPTHS.length; i++) {
        var d = DEPTHS[i];
        if (maxPct < d.t && pct >= d.t) once('scroll' + d.t, d.m, d.g, { percent: d.t });
      }
      maxPct = pct;
    }
    if (pct >= 40) reached40 = true;

    // DeepEvaluator: 40% depth AND >=30s of VISIBLE time (A2, 2026-08-31).
    // Was wall-clock since page start, which kept accruing in a backgrounded tab.
    // NOTE: delivery is still bound to the scroll handler, exactly as before - this
    // change is the clock only, per spec. It therefore still cannot fire for someone
    // who goes deep and then stops scrolling. Flagged, not changed.
    accrue();
    if (reached40 && visibleMs >= 30000) once('deep', 'DeepEvaluator', 'wellf_deep_evaluator', { percent: maxPct });
  }
  window.addEventListener('scroll', throttle(onScroll, 250), { passive: true });
  window.addEventListener('resize', throttle(onScroll, 250), { passive: true });
  onScroll();

  // =========================================================================
  // PlanCardDwell  ·  wellf_plancard_dwell   (A1, 2026-08-31)
  // REPLACES BuyBoxDwell, which was bound to [data-wellf-buybox] - the WHOLE product
  // column, 1,915px tall on mobile and 1,807px on desktop. Its intersectionRatio >= 0.5
  // branch was mathematically unreachable (it needed 957px visible in an 844px
  // viewport), so it always fell through to "422px of the column visible for 5s" and
  // qualified anywhere across a ~1,900px band. It measured presence in the product
  // area, not attention on the price.
  //
  // Now bound to the tightest container wrapping both plan cards (the [data-buy-mode]
  // parent, .pv2-opts on the PDP, measured ~212px tall: 1,043-1,255 mobile,
  // 476-670 desktop). That fits any viewport, so intersectionRatio genuinely works.
  // Renamed so pre/post rows can never be conflated.
  // =========================================================================
  var planZone = (function () {
    var first = document.querySelector('[data-buy-mode]');
    return first ? first.parentElement : null;
  })();
  var planQualified = false, planQualFrom = -1;
  if (planZone && 'IntersectionObserver' in window) {
    new IntersectionObserver(function (entries) {
      var e = entries[entries.length - 1];
      accrue();
      var nowQualified = e.isIntersecting && e.intersectionRatio >= 0.75;
      if (nowQualified && !planQualified) planQualFrom = visibleMs;   // start the window
      if (!nowQualified) planQualFrom = -1;                            // broke it, reset
      planQualified = nowQualified;
    }, { threshold: [0, 0.75] }).observe(planZone);
  }
  // Checked on the shared tick so an idle dwell (no scroll, no intersection change)
  // still resolves, and so the 1,500ms threshold is measured in VISIBLE time.
  function checkPlanDwell() {
    if (!planQualified || planQualFrom < 0 || fired['plandwell']) return;
    if (visibleMs - planQualFrom >= 1500) {
      once('plandwell', 'PlanCardDwell', 'wellf_plancard_dwell', { seconds: 1.5 });
    }
  }

  // =========================================================================
  // ADD TAPPED  ·  AddTapped / wellf_add_tapped   (2026-09-30)
  // The one thing the buy box never measured: the tap itself. add_to_cart comes from
  // Shopify's pixel AFTER the cart accepts the line, so a tap that produces no cart line
  // is indistinguishable from never having tapped, and the September Android
  // investigation had to be argued from code reading instead of one query. With this,
  // wellf_add_tapped minus add_to_cart is the answer directly.
  //
  // `control` separates the in-card button from the floating pill, which are different
  // targets with different hit areas. `tap_index` is kept because repeat taps ARE the
  // signal: someone tapping three times is someone who believes it is not working.
  // Capped so one frustrated session cannot dominate the dataset.
  //
  // Both controls submit a real form, so the page starts navigating immediately after
  // this fires. gtag and fbq both use sendBeacon where it exists, which survives unload;
  // where it does not, some taps will be lost and this undercounts. It never overcounts,
  // so the comparison against add_to_cart stays safe in the direction that matters.
  // =========================================================================
  var ADD_TAP_MAX = 5;
  var addTaps = 0;
  var addTapFire = debounced(400);
  document.addEventListener('click', function (e) {
    if (!e.target.closest) return;
    var fab = e.target.closest('[data-fab-add]');
    var rail = fab ? null : e.target.closest('[data-buy-add]');
    if (!fab && !rail) return;
    var btn = fab || rail;
    if (btn.disabled) return;   // sold out: not an attempt
    if (addTaps >= ADD_TAP_MAX) return;
    if (addTapFire('AddTapped', 'wellf_add_tapped', {
      control: fab ? 'fab' : 'rail',
      tap_index: addTaps + 1
    })) addTaps++;
  }, { passive: true, capture: true });

  // =========================================================================
  // PLAN TOGGLE  ·  PlanToggler  (repeatable, debounced 1s, change-only, capped)
  // Measures plan COMPARISON, so only an actual change of the selected plan counts.
  // Re-clicking the plan already selected is a no-op click, not a comparison, and
  // used to fire anyway. Capped per pageview so one indecisive session can't dominate
  // the dataset; toggle_index says which toggle it was. See EVENT-NAME MAP up top.
  // =========================================================================
  var PLAN_TOGGLE_MAX = 4;
  var planFire = debounced(1000);
  var planToggles = 0;
  var planMode = (function () {
    var pressed = document.querySelector('[data-buy-mode][aria-pressed="true"]');
    return pressed ? pressed.getAttribute('data-buy-mode') : null;
  })();
  Array.prototype.forEach.call(document.querySelectorAll('[data-buy-mode]'), function (btn) {
    btn.addEventListener('click', function () {
      var mode = btn.getAttribute('data-buy-mode');
      if (mode === planMode) return;          // same plan re-clicked: not a toggle
      planMode = mode;                        // track state even once we stop sending
      if (planToggles >= PLAN_TOGGLE_MAX) return;
      var sent = planFire('PlanToggler', 'wellf_plan_toggle', {
        selected: mode === 'sub' ? 'club' : 'onetime',
        toggle_index: planToggles + 1
      });
      if (sent) planToggles++;                // count deliveries, not clicks
    }, { passive: true });
  });

  // =========================================================================
  // GALLERY SWIPE  ·  GallerySwipe  (from the 3rd image change, debounced 1s)
  // =========================================================================
  var track_ = document.getElementById('pdpTrack');
  if (track_) {
    var galleryFire = debounced(1000);
    var galIndex = Math.round(track_.scrollLeft / Math.max(1, track_.clientWidth));
    var galChanges = 0;
    // Single source of truth = the settled image index; counted once per DISTINCT
    // index so a scroll event and its follow-up click re-check can't double-count.
    function galleryCheck() {
      var idx = Math.round(track_.scrollLeft / Math.max(1, track_.clientWidth));
      if (idx === galIndex) return;
      galIndex = idx;
      galChanges++;
      if (galChanges >= 3) galleryFire('GallerySwipe', 'wellf_gallery_swipe', { detail: idx });
    }
    // Touch swipe scrolls the track directly.
    track_.addEventListener('scroll', throttle(galleryCheck, 200), { passive: true });
    // Arrow / thumb clicks scroll it smoothly (arrows are hidden on touch); re-check
    // after the ~400ms smooth-scroll settles, in case scroll events are missed.
    Array.prototype.forEach.call(
      document.querySelectorAll('[data-gallery-prev], [data-gallery-next], [data-gallery-thumb]'),
      function (el) { el.addEventListener('click', function () { setTimeout(galleryCheck, 450); }, { passive: true }); }
    );
  }

  // =========================================================================
  // OBJECTION  ·  FAQ items + Ingredients/Nutrition accordion
  // =========================================================================
  // Keys mirror data-wellf-faq computed in wellf-product-faq.liquid. Names renamed
  // 2026-08-15 to match the FAQ each actually binds (see EVENT-NAME MAP up top).
  var FAQ_MAP = {
    timeline:       { m: 'FAQ_WhenDoesItWork', g: 'wellf_faq_timeline' },
    trust_gummies:  { m: 'FAQ_TrustGummies',   g: 'wellf_faq_trust_gummies' },
    subscription:   { m: 'FAQ_Subscription',   g: 'wellf_faq_subscription' },
    sweet_vs_gummy: { m: 'FAQ_SweetVsGummy',   g: 'wellf_faq_sweet_vs_gummy' }
  };
  Array.prototype.forEach.call(document.querySelectorAll('[data-wellf-faq]'), function (item) {
    var key = item.getAttribute('data-wellf-faq');
    var map = FAQ_MAP[key];
    if (!map) return;
    var btn = item.querySelector('[data-faq-q]') || item;
    btn.addEventListener('click', function () {
      // fire on OPEN only (item toggles to data-open="true"); check next frame
      setTimeout(function () {
        if (item.getAttribute("data-open") === "true") once('faq_' + key, map.m, map.g, { detail: key });
      });
    }, { passive: true });
  });

  // Ingredients / Nutrition accordion (.acc__item) ; Third-party testing -> Eurofins
  Array.prototype.forEach.call(document.querySelectorAll('.acc__item'), function (item) {
    var label = ((item.querySelector('.acc__q-text') || {}).textContent || '').trim().toLowerCase();
    var btn = item.querySelector('[data-acc-q]') || item;
    btn.addEventListener('click', function () {
      setTimeout(function () {
        if (item.getAttribute('data-open') !== 'true') return;   // only on open
        if (label.indexOf('ingredient') > -1 || label.indexOf('nutrition') > -1) {
          once('ingredients', 'IngredientsOpen', 'wellf_ingredients_open', { detail: label });
        } else if (label.indexOf('third') > -1 || label.indexOf('testing') > -1 || label.indexOf('eurofins') > -1) {
          once('eurofins', 'EurofinsClick', 'wellf_eurofins_click', { detail: 'accordion' });
        }
      });
    }, { passive: true });
  });

  // =========================================================================
  // TRUST  ·  EurofinsClick (lab report) / ReviewsReached / ReviewExpand
  // =========================================================================
  document.addEventListener('click', function (e) {
    if (e.target.closest && e.target.closest('[data-wellf-modal-open="lab-report"]')) {
      once('eurofins', 'EurofinsClick', 'wellf_eurofins_click', { detail: 'lab-report' });
    }
  }, { passive: true });

  // Market price comparison (i): fires only on a tap/click of the info button that
  // opens the explainer modal (never on pageview). Fire-once per pageview, like the others.
  document.addEventListener('click', function (e) {
    if (e.target.closest && e.target.closest('[data-wellf-mktinfo]')) {
      once('mktinfo', 'MarketComparisonInfo', 'wellf_market_comparison_info', { detail: 'pdp' });
    }
  }, { passive: true });

  // Bound to the reviews HEADING, not to .prv. On 1 Sep the taste-reel teaser was added
  // as the first child of .prv, which grew that box from 185px to 326px on mobile, so a
  // threshold of 0.2 against .prv started firing when the TEASER came into view, roughly
  // 150px before the reviews themselves. The heading is the first reviews element, so it
  // restores what the event name claims. Falls back to the old targets if the markup moves.
  // TWO elements, not one. They were the same variable and that was a bug: narrowing it to
  // .prv__head on 1 Sep for the observer silently killed ReviewNav, because the arrows live
  // in .prv__stage, a SIBLING of the head, so the delegated click could never see them.
  // reviewsAnchor = what "reached the reviews" means: the heading coming into view.
  // reviewsRoot   = the container the arrows are actually inside.
  var reviewsAnchor = document.querySelector('.prv__head') || document.querySelector('.prv') || document.querySelector('[data-wellf-reviews]');
  var reviewsRoot = document.querySelector('.prv') || document.querySelector('[data-wellf-reviews]') || reviewsAnchor;
  var reviews = reviewsAnchor;
  if (reviews && 'IntersectionObserver' in window) {
    var ro = new IntersectionObserver(function (entries) {
      if (entries[0].isIntersecting) {
        once('reviews_reached', 'ReviewsReached', 'wellf_reviews_reached', {});
        ro.disconnect();
      }
    }, { threshold: 0.2 });
    ro.observe(reviews);
    // ReviewNav: nav arrows on the compact PDP slider. Delegated from reviewsRoot, which
    // contains them; reviews/reviewsAnchor is only the heading.
    reviewsRoot.addEventListener('click', function (e) {
      // [data-rev-expand] matched ZERO elements in the live DOM, so the "expand" path
      // never existed. Selector dropped and the event renamed to what it actually
      // measures: clicks on the slider's prev/next arrows.
      if (e.target.closest && e.target.closest('[data-rev-next], [data-rev-prev]')) {
        once('review_nav', 'ReviewNav', 'wellf_review_nav', {});
      }
    }, { passive: true });
  }

  // =========================================================================
  // ENGAGEMENT  ·  Engaged30s / Engaged90s (visible-time) + PDPVideoPlay
  // =========================================================================
  // BUG FOUND (2026-08-15): Engaged90s reached Meta 0× in 3+ days while Engaged30s
  // flowed (~27). Root cause: DELIVERY was bound only to the 1s setInterval tick.
  // Engaged30s is almost always crossed inside one unbroken foreground burst, so its
  // delivering tick fires. Engaged90s typically crosses right as the visitor is about
  // to leave/background — on mobile the tab is then FROZEN (iOS app-switch / tab
  // suspend / bfcache), so the interval tick that would deliver eng90 never runs and
  // the crossing is lost. It was NOT a sink split (both sinks share one track() call)
  // and NOT the fire-once Set (eng30/eng90 use distinct keys). Fix: accrue AND
  // check-and-fire on the hidden transition (visibilitychange) and on pagehide, so a
  // crossing is delivered the instant it happens, independent of a surviving tick.
  function checkEngagement() {
    accrue();
    if (visibleMs >= 30000) once('eng30', 'Engaged30s', 'wellf_engaged_30s', { seconds: 30 });
    if (visibleMs >= 90000) once('eng90', 'Engaged90s', 'wellf_engaged_90s', { seconds: 90 });
    checkPlanDwell();          // same clock, same tick
  }
  document.addEventListener('visibilitychange', function () {
    // Going hidden: accrue the final foreground chunk and FIRE before the tab can
    // freeze (uses the pre-change `visible`); then flip. Returning: no-op accrue.
    checkEngagement();
    visible = (document.visibilityState !== 'hidden');
  });
  window.addEventListener('pagehide', checkEngagement, { passive: true });
  setInterval(checkEngagement, 250);    // 250ms so the 1,500ms PlanCardDwell threshold is accurate

  // ── PDPVideoPlay · RETIRED 2026-09-01, nothing below emits it.
  // History, because every historical row needs reading correctly: it used to bind
  // `#formulaVideo || .pdp video`, and wellf-product.js play()s that video from an
  // IntersectionObserver at 25% visibility. So the event fired on SCROLL, not intent,
  // recording "reached mid-page" (51% depth desktop, 48% mobile). That is why
  // Jaccard(video_play, price_survivor) was 0.86 and video_play -> price_survivor was
  // 25/25. Every row under this name is scroll-proxy data.
  // It was detached on 31 Aug and briefly reserved for the reel. It is not reserved: the
  // reel is measured by wellf_reel_start and wellf_reel_progress, which carry the depth
  // this name never did. Do not revive it. ──
  // ================== THE TASTE REEL ==================
  // The only video on this page we measure. The formula-section gummy loop is ambient
  // autoplay wallpaper with no intent in it, deliberately left untracked: it carries no
  // data-wellf-video-engagement attribute and nothing below will ever see it.
  //
  // Delegated capture-phase listeners, not a query at init: the reel builds its <video>
  // inside the first click handler, so a query-once binding would find nothing and every
  // event here would be permanently dead.
  //
  // Four signals, which together answer "did the thumbnail work, did they press play,
  // and how far did they get":
  //   teaser_click -> start -> progress(10/25/50/75/95) -> watch (summary at exit)
  (function () {
    var REEL_STEPS = [10, 25, 50, 75, 95];

    var gestureAt   = -Infinity;
    var viaTeaser   = false;   // did a teaser click precede the play, in this pageview
    var plays       = 0;       // number of times playback was started
    var maxPct      = 0;       // furthest point reached, monotonic
    var watchedMs   = 0;       // accumulated real playback time
    var playingFrom = 0;       // perfNow() at the last `playing`
    var isPlaying   = false;
    var armed       = false;   // did a gesture precede this playback (set on `play`)
    var started     = false;
    var completed   = false;

    function isReelVideo(el) {
      try { return !!(el && el.tagName === 'VIDEO' && el.hasAttribute('data-wellf-video-engagement')); }
      catch (e) { return false; }
    }

    // ── the buy-box thumbnail. Scrolls to the section; measured as its own intent
    // signal because "the thumbnail earned a tap" and "the reel earned a play" are
    // different questions and the drop-off between them is the thing worth knowing. ──
    document.addEventListener('click', function (e) {
      try {
        var t = e.target;
        if (!t || !t.closest || !t.closest('[data-wellf-reel-teaser]')) return;
        viaTeaser = true;
        once('reel_teaser_click', 'ReelTeaserClick', 'wellf_reel_teaser_click', {
          scroll_pct: scrollPct()
        });
      } catch (err) {}
    }, true);

    function markGesture(e) {
      try {
        var t = e.target;
        if (!t || !t.closest) return;
        if (isReelVideo(t) ||
            t.closest('[data-wellf-video-engagement]') ||
            t.closest('[data-wellf-video-control]') ||
            t.closest('[data-wellf-reel]')) {
          gestureAt = perfNow();
        }
      } catch (err) {}
    }
    document.addEventListener('pointerdown', markGesture, true);
    document.addEventListener('keydown', markGesture, true);

    function accruePlayback() {
      if (isPlaying) { watchedMs += perfNow() - playingFrom; isPlaying = false; }
    }

    // `play`, `playing`, `pause`, `ended` and `timeupdate` do not bubble, so every one
    // of these must be capture phase on document.
    // The gesture gate from the 31 Aug detach: playback that no tap preceded is not
    // intent, which is what stopped the old event being a scroll-depth proxy.
    //
    // It is evaluated on `play`, which fires synchronously with the play() call inside
    // the tap handler, NOT on `playing`. `playing` waits for enough buffer, and on a
    // slow connection that is comfortably more than a second after the tap, so timing
    // the window from there would silently drop genuine plays on exactly the
    // connections we most want to hear about. Verified: a real CDN load took ~1.4s.
    document.addEventListener('play', function (e) {
      if (!isReelVideo(e.target)) return;
      armed = (perfNow() - gestureAt <= 1000);
    }, true);

    document.addEventListener('playing', function (e) {
      var v = e.target;
      if (!isReelVideo(v) || !armed) return;
      plays++;
      playingFrom = perfNow();
      isPlaying = true;
      if (!started) {
        started = true;
        once('reel_start', 'ReelStart', 'wellf_reel_start', {
          via: viaTeaser ? 'teaser' : 'direct',
          sound: v.muted ? 'muted' : 'on'
        });
      }
    }, true);

    document.addEventListener('timeupdate', function (e) {
      var v = e.target;
      if (!isReelVideo(v) || !started) return;
      try {
        if (!v.duration || !isFinite(v.duration)) return;
        var pct = Math.floor((v.currentTime / v.duration) * 100);
        if (pct <= maxPct) return;
        maxPct = Math.min(100, pct);
        for (var i = 0; i < REEL_STEPS.length; i++) {
          var step = REEL_STEPS[i];
          if (maxPct >= step) {
            // 10% is the accidental-tap filter: on a 25s reel that is 2.5s, which is
            // roughly where the old 2s gate sat. Kept inside progress rather than as its
            // own event so the whole retention curve reads from one place.
            once('reel_p' + step, 'ReelProgress', 'wellf_reel_progress', { pct: step });
          }
        }
      } catch (err) {}
    }, true);

    document.addEventListener('pause', function (e) {
      if (isReelVideo(e.target)) accruePlayback();
    }, true);

    document.addEventListener('ended', function (e) {
      if (!isReelVideo(e.target)) return;
      accruePlayback();
      completed = true;
      maxPct = 100;
      once('reel_p95', 'ReelProgress', 'wellf_reel_progress', { pct: 95 });
    }, true);

    // ── the summary. Sent at every exit-ish moment, not once. ──
    // It used to be once() per pageview, described as "at exit". It is not at exit: it is
    // also wired to visibilitychange, and a tab backgrounded MID-WATCH is a hidden
    // transition like any other. So the first time someone switched away the summary was
    // sent and then frozen, while the progress steps carried on advancing when they came
    // back. Observed live 2026-09-07: a session sent max_pct 10 / watched_ms 3513, then
    // fired progress 25 and 50 afterwards, with away_return either side of the gap. Both
    // numbers were understated, not just max_pct.
    //
    // Now it re-sends whenever the figures have actually MOVED, carrying `seq`. Reading
    // rule: take the row with the highest `seq` per pageview, or simply MAX(max_pct) and
    // MAX(watched_ms) per session; both are monotonic within a pageview. The equality
    // guard keeps a background/foreground cycle with no further watching from adding rows.
    var summarySeq = 0;
    var lastSentPct = -1;
    var lastSentMs  = -1;

    function sendWatchSummary() {
      if (!started) return;
      accruePlayback();
      var ms = Math.round(watchedMs);
      if (maxPct === lastSentPct && ms === lastSentMs) return;
      lastSentPct = maxPct;
      lastSentMs  = ms;
      summarySeq++;
      track('ReelWatch', 'wellf_reel_watch', {
        max_pct: maxPct,
        watched_ms: ms,
        plays: plays,
        completed: completed ? 1 : 0,
        seq: summarySeq
      });
    }
    window.addEventListener('pagehide', sendWatchSummary, { capture: true });
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'hidden') sendWatchSummary();
    }, true);
  })();

  // =========================================================================
  // WELCOME CREDIT  ·  the £5 new-customer funnel   (2026-09-04)
  // The line itself is a plain delegated click, like EurofinsClick. Everything after
  // it happens inside the modal, where assets/wellf-welcome.js owns the state machine
  // and dispatches wellf:welcome CustomEvents. Grading them here keeps consent, the
  // GA4/Meta sinks and the caps in one place, and means the modal module can stay
  // measurement free.
  //
  // The submitted address never leaves that module. Nothing below reads it.
  // =========================================================================
  var WELCOME_TOGGLE_MAX = 4;
  var wcOpenedAt = -1;
  var wcToggleFire = debounced(1000);
  var wcToggles = 0;

  document.addEventListener('click', function (e) {
    if (e.target.closest && e.target.closest('[data-wellf-modal-open="welcome"]')) {
      if (wcOpenedAt < 0) wcOpenedAt = perfNow();
      once('wc_line', 'WelcomeLineClick', 'wellf_welcome_line_click', { detail: 'pdp', via: 'manual' });
    }
  }, { passive: true });

  // =========================================================================
  // Club perk zoom (the perks rail thumbnails).
  // The rail sits in the gap between the mktcomp and guarantee zones, so it accrues no
  // zone dwell of its own. A tap on a thumbnail is the ONLY signal that anyone looked
  // at what the Club actually gives them, which is why this is worth an event at all.
  //
  // Repeatable and capped rather than fire-once: opening three perks in a row is a
  // different and more interesting act than opening one, and `zoom_index` preserves
  // that without a second event. The 600ms gap is a double-tap guard, not a sampler.
  //
  // `perk` is the caption the thumbnail already carries, so it stays in step with the
  // rail copy automatically and cannot drift into high cardinality: there are four.
  // The homepage gifts ladder carries the same thumbnails but this file never loads
  // there, so those taps are NOT counted here. See the note in the reference doc.
  // =========================================================================
  var PERK_ZOOM_MAX = 6;
  var perkZooms = 0;
  var perkZoomFire = debounced(600);

  document.addEventListener('click', function (e) {
    var opener = e.target.closest && e.target.closest('[data-wellf-modal-open="perk"]');
    if (!opener) return;
    if (perkZooms >= PERK_ZOOM_MAX) return;
    if (perkZoomFire('PerkZoom', 'wellf_perk_zoom', {
      perk: opener.getAttribute('data-modal-caption') || 'unknown',
      zoom_index: perkZooms + 1
    })) perkZooms++;
  }, { passive: true });

  document.addEventListener('wellf:welcome', function (e) {
    var d = (e && e.detail) || {};
    var inModal = wcOpenedAt < 0 ? null : Math.round(perfNow() - wcOpenedAt);
    // `via` says whether the visitor found the line themselves or the trigger opened it for
    // them. Without it the two are indistinguishable and the claim rate is uninterpretable,
    // because an auto-open has no tap in front of it to divide by. Read every welcome metric
    // split on this. See the discontinuity note in the reference doc.
    var via = d.via === 'auto' ? 'auto' : 'manual';

    // The auto-open is the denominator for everything below it. wellf_welcome_line_click
    // only ever means "they found the line and tapped it", so an automatic open has no
    // impression event of its own without this one.
    if (d.type === 'auto_shown') {
      // Start the in-modal clock here too. It was only ever started by a click on the line,
      // so without this every auto-opened session reports ms=null on use and dismiss - and
      // "how long did they sit in a modal they did not ask for before closing it" is the
      // single best read on whether the auto-open is welcome or irritating.
      if (wcOpenedAt < 0) wcOpenedAt = perfNow();
      once('wc_auto_shown', 'WelcomeAutoShown', 'wellf_welcome_auto_shown', {
        qualified_by: d.qualified_by, fired_by: d.fired_by
      });
      return;
    }
    if (d.type === 'claim') {
      once('wc_claim', 'WelcomeClaim', 'wellf_welcome_claim', { via: via });
      return;
    }
    if (d.type === 'plan_toggle') {
      if (wcToggles >= WELCOME_TOGGLE_MAX) return;
      if (wcToggleFire('WelcomePlanToggle', 'wellf_welcome_plan_toggle', {
        plan: d.plan, toggle_index: wcToggles + 1, via: via
      })) wcToggles++;
      return;
    }
    if (d.type === 'use') {
      once('wc_use', 'WelcomeUse', 'wellf_welcome_use', {
        via: via,
        plan: d.plan,
        plan_changed: d.plan_changed ? 1 : 0,
        ms: inModal
      });
      return;
    }
    // The trigger decided to open the modal and the open threw, so the visitor was
    // qualified and got nothing. Without this the failure is indistinguishable from never
    // having qualified, which is why it went unnoticed.
    if (d.type === 'auto_open_failed') {
      once('wc_auto_failed', 'WelcomeAutoFailed', 'wellf_welcome_auto_failed', {
        qualified_by: d.qualified_by, fired_by: d.fired_by
      });
      return;
    }
    // They claimed, tapped use, and the cart add did not land.
    if (d.type === 'use_failed') {
      once('wc_use_failed', 'WelcomeUseFailed', 'wellf_welcome_use_failed', {
        via: via, plan: d.plan, ms: inModal
      });
      return;
    }
    if (d.type === 'dismiss') {
      // Once per stage: reopening and closing again is the same drop-off, not a new one.
      once('wc_dismiss_' + d.stage, 'WelcomeDismiss', 'wellf_welcome_dismiss', {
        via: via,
        stage: d.stage,
        ms: inModal
      });
      return;
    }
    if (d.type === 'invalid_email') {
      once('wc_email_err', 'WelcomeEmailError', 'wellf_welcome_email_error', { via: via });
    }
  }, { passive: true });

  if (DEBUG) { try { console.log('[wellf-events] armed (consent gated).'); } catch (e) {} }
})();
